import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"
import { Alert, Button, Snackbar } from "@mui/material"

/**
 * "Deleted. Undo?" — instead of "Are you sure?"
 *
 * ── Why this beats a confirmation dialog ────────────────────────────────────
 *
 * A confirmation taxes every action to prevent the rare mistake, and it does
 * not even work: people learn the shape of the dialog and click through it
 * without reading, which means the tax is paid and the protection is not.
 * Undo reverses the burden — the common case costs nothing, and the rare
 * mistake is recoverable.
 *
 * Destructive-and-irreversible still gets a dialog. Voiding an invoice a client
 * has seen is not undoable by deleting a row, and offering Undo for it would be
 * a promise this cannot keep.
 *
 * ── Why the action runs immediately ─────────────────────────────────────────
 *
 * The alternative — hold it for five seconds and then commit — means the list
 * disagrees with the server for as long as the toast is up, every navigation
 * has to flush the queue, and closing the tab loses the change. So the action
 * happens, and Undo is a second call that reverses it. Which means `undo` has
 * to be a real inverse, and a caller that cannot write one should not offer it.
 */
const UndoContext = createContext(null)

export function UndoProvider({ children }) {
  const [toast, setToast] = useState(null)
  const undoing = useRef(false)

  const offer = useCallback((message, undo) => {
    undoing.current = false
    setToast({ message, undo, key: Date.now() })
  }, [])

  const run = async () => {
    if (!toast?.undo || undoing.current) return
    undoing.current = true
    try {
      await toast.undo()
    } finally {
      setToast(null)
    }
  }

  const value = useMemo(() => ({ offer }), [offer])

  return (
    <UndoContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.key}
        open={Boolean(toast)}
        autoHideDuration={8000}
        onClose={(event, reason) => {
          // Not on a click elsewhere: the whole point is that it survives
          // long enough to be noticed and pressed.
          if (reason !== "clickaway") setToast(null)
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ mb: { xs: 8, md: 2 } }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ alignItems: "center", boxShadow: 6 }}
          action={
            toast?.undo ? (
              <Button color="inherit" size="small" onClick={run} sx={{ fontWeight: 700 }}>
                Undo
              </Button>
            ) : null
          }
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </UndoContext.Provider>
  )
}

/** `offer("Task deleted.", () => taskApi.restore(id))` */
export function useUndo() {
  const context = useContext(UndoContext)
  /* A no-op rather than a throw: a component rendered in a test or a story
     without the provider should still work, and nothing here is load-bearing. */
  return context || { offer: () => {} }
}
